import { ChangeDetectionStrategy, Component, inject, computed } from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CrwdFloatingNotice, CrwdFloatingNoticeStack } from '@crewdle/ui-kit';

import { LocalFilesGrantService } from 'src/app/services/local-files/local-files-grant.service';
import { LocalFilesService } from 'src/app/services/local-files/local-files.service';

/**
 * The just-in-time shared-folder cards (11-local-files.md): rendered app-wide so a
 * grant request surfaces wherever the user is — the asking chat, another chat, the
 * dashboard. Renders nothing when nothing is pending, which is also the web/mobile
 * case (requests only originate inside the desktop shell).
 */
@Component({
  selector: 'app-local-files-grant-banner',
  standalone: true,
  imports: [TranslateModule, CrwdFloatingNotice, CrwdFloatingNoticeStack],
  changeDetection: ChangeDetectionStrategy.Eager,
  template: `
    <!-- The kit's floating-notice stack (top-centre, safe-area aware): one card per pending request. -->
    @if (grants.pending().length) {
      <crwd-floating-notice-stack placement="top-center">
        @for (request of grants.pending(); track request.id) {
          <crwd-floating-notice
            data-testid="local-files-grant-card"
            detailTruncate
            [title]="'local_files.grant-title' | translate: { origin: originLabel(request.origin) }"
            [detail]="detailFor(request.requestedPath)"
            [actionLabel]="'local_files.grant-share' | translate"
            [dismissLabel]="'local_files.grant-dismiss' | translate"
            (pressed)="grants.share(request)"
            (dismissed)="grants.dismiss(request)"
          />
        }
      </crwd-floating-notice-stack>
    }
  `,
  // Connect's accent on Share, as before the move (the white-label accent is not applied: deviation D7).
  styles: `
    :host { --crwd-notice-action-bg: var(--color-accent, #4c17ca); }
  `,
})
export class LocalFilesGrantBannerComponent {
  readonly grants = inject(LocalFilesGrantService);
  private readonly translate = inject(TranslateService);
  private readonly localFiles = inject(LocalFilesService);
  readonly sharedCount = computed(() => this.localFiles.roots().length);

  /** The card's second line: the requested path, else what sharing gives the agent. */
  detailFor(requestedPath: string | null | undefined): string {
    if (requestedPath) return this.translate.instant('local_files.grant-detail-path', { path: requestedPath });
    const count = this.sharedCount();
    return count === 0 ? this.translate.instant('local_files.grant-detail') : this.translate.instant('local_files.grant-detail-more', { count });
  }

  /** '@'-prefixed origins are i18n keys; anything else is an agent's own name. */
  originLabel(origin: string): string {
    return origin.startsWith('@') ? this.translate.instant(`local_files.origin.${origin.slice(1)}`) : origin;
  }
}
